import { getIntersectionRoot } from '@theme/scroll-container';

/**
 * Glide home page motion: reveals sections as they scroll into view and counts up the waitlist number.
 * Only elements that start below the fold are tagged, so nothing visible on load ever flickers.
 */

const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
const STAGGER_MS = 90;
const MAX_STAGGER_STEPS = 4;
const COUNTER_HEADING = /^(?:\+|plus)\s*(?:de\s*)?\d/i;
const COUNTER_NUMBER = /\d(?:[\d\s ]*\d)?/;

/** @param {Element} element */
function startsBelowFold(element) {
  return element.getBoundingClientRect().top > window.innerHeight * 0.9;
}

/**
 * Collects the elements to reveal, section by section, skipping the hero (it has its own entrance).
 * A row of cards is revealed card by card instead of as one block.
 *
 * @param {HTMLElement} main
 * @returns {{ element: HTMLElement, kind: string }[][]} Groups of siblings that share a stagger.
 */
function collectTargets(main) {
  const groups = [];
  const sections = [...main.querySelectorAll(':scope > .shopify-section')].slice(1);

  for (const section of sections) {
    const cards = [...section.querySelectorAll('product-card, .featured-blog-posts-card')];
    if (cards.length) {
      groups.push(cards.map((element) => ({ element: /** @type {HTMLElement} */ (element), kind: 'card' })));
      continue;
    }

    const wrapper = section.querySelector('.section-content-wrapper');
    if (wrapper) {
      const items = [];
      for (const child of wrapper.children) {
        if (!(child instanceof HTMLElement) || child.matches('script, style')) continue;
        const cards = child.querySelectorAll(':scope > .layout-panel-flex--row > .group-block');
        if (cards.length > 1) {
          groups.push([...cards].map((card) => ({ element: /** @type {HTMLElement} */ (card), kind: 'card' })));
        } else {
          items.push({ element: child, kind: 'block' });
        }
      }
      groups.push(items);
      continue;
    }

    const media = [...section.querySelectorAll('.media-block')].map((element) => ({ element, kind: 'media' }));
    const contents = [...section.querySelectorAll('.section > .group-block')].map((element) => ({ element, kind: 'block' }));
    groups.push([...media, ...contents]);
  }

  return groups.map((group) =>
    group.filter((target) => target.element instanceof HTMLElement && startsBelowFold(target.element))
  );
}

/**
 * Finds headings written as "+ de 700 …" and returns the text node holding the number, split around it.
 *
 * @param {HTMLElement} main
 * @returns {{ heading: HTMLElement, node: Text, prefix: string, target: number, suffix: string }[]}
 */
function findCounters(main) {
  const counters = [];
  for (const heading of main.querySelectorAll('h2, h3')) {
    if (!(heading instanceof HTMLElement) || !COUNTER_HEADING.test(heading.textContent?.trim() ?? '')) continue;

    const walker = document.createTreeWalker(heading, NodeFilter.SHOW_TEXT);
    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
      const match = node.textContent?.match(COUNTER_NUMBER);
      if (!match || match.index === undefined) continue;
      const text = node.textContent ?? '';
      counters.push({
        heading,
        node: /** @type {Text} */ (node),
        prefix: text.slice(0, match.index),
        target: Number(match[0].replace(/\D/g, '')),
        suffix: text.slice(match.index + match[0].length),
      });
      break;
    }
  }
  return counters.filter((counter) => counter.target > 0);
}

/** @param {{ heading: HTMLElement, node: Text, prefix: string, target: number, suffix: string }} counter */
function countUp({ heading, node, prefix, target, suffix }) {
  const format = new Intl.NumberFormat(document.documentElement.lang || 'fr');
  const duration = 1800;
  let start;

  /** @param {number} now */
  const step = (now) => {
    start ??= now;
    const progress = Math.min((now - start) / duration, 1);
    const eased = 1 - Math.pow(1 - progress, 4);
    node.textContent = `${prefix}${format.format(Math.round(target * eased))}${suffix}`;
    if (progress < 1) {
      requestAnimationFrame(step);
    } else {
      heading.classList.add('is-counted');
    }
  };

  requestAnimationFrame(step);
}

function init() {
  if (REDUCED_MOTION.matches || !('IntersectionObserver' in window)) return;

  const main = document.querySelector('main');
  if (!(main instanceof HTMLElement)) return;

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const element = /** @type {HTMLElement} */ (entry.target);
        element.classList.add('is-revealed');
        observer.unobserve(element);

        // Hand transitions back to the theme (e.g. product card hover lift) once the reveal is done.
        const delay = parseFloat(element.style.getPropertyValue('--glide-reveal-delay')) || 0;
        const duration = element.dataset.glideReveal === 'media' ? 1700 : 1000;
        setTimeout(() => {
          delete element.dataset.glideReveal;
          element.style.removeProperty('--glide-reveal-delay');
          element.classList.remove('is-revealed');
        }, delay + duration);
      }
    },
    { root: getIntersectionRoot(), rootMargin: '0px 0px -8% 0px', threshold: 0 }
  );

  for (const group of collectTargets(main)) {
    group.forEach(({ element, kind }, index) => {
      element.dataset.glideReveal = kind;
      element.style.setProperty('--glide-reveal-delay', `${Math.min(index, MAX_STAGGER_STEPS) * STAGGER_MS}ms`);
      observer.observe(element);
    });
  }

  // Counters animate whenever they are first well in view, even if they were already on screen at load.
  const counters = findCounters(main);
  const counterObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        counterObserver.unobserve(entry.target);
        const counter = counters.find(({ heading }) => heading === entry.target);
        if (counter) countUp(counter);
      }
    },
    { root: getIntersectionRoot(), threshold: 0.6 }
  );

  for (const counter of counters) {
    counter.heading.classList.add('glide-counter');
    // Start from zero only while the number is still out of sight, so nobody sees it jump back.
    if (startsBelowFold(counter.heading)) {
      counter.node.textContent = `${counter.prefix}0${counter.suffix}`;
    }
    counterObserver.observe(counter.heading);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
